import { useCallback, useMemo } from "react";

import { filter } from "lodash";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { TextField, Typography } from "@mui/material";

import { IAttribute } from "@/api";

import { AttributeTag } from "../../AttributeTag/AttributeTag";
import { Autocomplete, Checkbox } from "../../TrackedComponents";
import { TShiftEditorProps } from "../types";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export const PositionPicker = (props: TShiftEditorProps) => {
  const { staffShift, updateStaffShift, unitOfShift, readonly, staffDetails } = props;

  // Attributes selector
  const availableAttributes = useMemo(() => {
    const unitAttributes = unitOfShift.attributes;
    return filter(unitAttributes, ({ key }) => staffDetails.attributeKeys.includes(key));
  }, [staffDetails.attributeKeys, unitOfShift.attributes]);
  const selectedAttributes = useMemo(
    () => availableAttributes.filter(({ key }) => staffShift.attributes.includes(key)),
    [availableAttributes, staffShift.attributes],
  );

  const renderTags = useCallback((value: IAttribute[]) => {
    const maxTags = 3;
    const tags = value.slice(0, maxTags);
    const remainingTags = value.length - maxTags;

    if (value.length === 1) {
      return (
        <Typography
          className="single-attribute-tag"
          onClick={(event) => {
            // Propagate click to the autocomplete dropdown button
            (event.target as HTMLDivElement).parentElement?.parentElement
              ?.getElementsByTagName("button")?.[0]
              ?.click();
          }}
        >
          {value[0]!.name}
        </Typography>
      );
    }

    return (
      <>
        {tags.map((attribute) => (
          <AttributeTag key={attribute.key} attribute={attribute} />
        ))}
        {remainingTags > 0 && <span>+{remainingTags}</span>}
      </>
    );
  }, []);

  return (
    <Autocomplete
      size="small"
      className={`attributes-picker ${selectedAttributes.length === 0 ? "empty" : ""}`}
      trackingLabel="attributes-picker"
      readOnly={readonly}
      disabled={readonly}
      multiple
      options={availableAttributes}
      value={selectedAttributes}
      disableCloseOnSelect
      disableClearable
      getOptionLabel={({ name }) => name}
      renderOption={(autocompleteProps, option, { selected }) => (
        <li {...autocompleteProps}>
          <Checkbox
            trackingLabel="attribute-picker"
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.name}
        </li>
      )}
      renderTags={renderTags}
      renderInput={(params) => <TextField {...params} label="Position" />}
      onChange={(_event, value) => {
        updateStaffShift({
          ...staffShift,
          attributes: value.map((attribute) => attribute.key),
        });
      }}
    />
  );
};
